(() => {
  const MODAL_SELECTOR = '.msh-sgv';
  const OPEN_ATTR = 'data-size-guide-open';
  const MIN_SCALE = 1;
  const MAX_SCALE = 3;
  const DOUBLE_TAP_DELAY = 300;

  const distanceBetweenTouches = (touchA, touchB) => {
    const dx = touchA.clientX - touchB.clientX;
    const dy = touchA.clientY - touchB.clientY;
    return Math.hypot(dx, dy);
  };

  const initModal = (modal) => {
    if (!modal || modal.dataset.sizeGuideInit === 'true') return;

    const modalId = modal.id;
    const openButton = document.querySelector(`[${OPEN_ATTR}="${modalId}"]`);
    const closeButtons = modal.querySelectorAll('[data-size-guide-close]');
    const modalBody = modal.querySelector('.msh-sgv__body');
    const image = modal.querySelector('.msh-sgv__image');

    if (!modalBody || !image) return;

    const state = {
      scale: MIN_SCALE,
      translateX: 0,
      translateY: 0,
      pinchStartDistance: 0,
      pinchStartScale: MIN_SCALE,
      panStartX: 0,
      panStartY: 0,
      panOriginX: 0,
      panOriginY: 0,
      isPointerPanning: false,
      isTouchPanning: false,
      lastTapTime: 0,
    };

    const getSize = () => ({
      bodyWidth: modalBody.clientWidth,
      bodyHeight: modalBody.clientHeight,
      imageWidth: image.clientWidth,
      imageHeight: image.clientHeight,
    });

    const clamp = () => {
      if (state.scale <= MIN_SCALE) {
        state.translateX = 0;
        state.translateY = 0;
        return;
      }

      const { bodyWidth, bodyHeight, imageWidth, imageHeight } = getSize();
      const scaledWidth = imageWidth * state.scale;
      const scaledHeight = imageHeight * state.scale;
      const minX = Math.min(0, bodyWidth - scaledWidth);
      const minY = Math.min(0, bodyHeight - scaledHeight);

      state.translateX = Math.min(0, Math.max(minX, state.translateX));
      state.translateY = Math.min(0, Math.max(minY, state.translateY));
    };

    const render = () => {
      clamp();
      image.style.transform = `translate3d(${state.translateX}px, ${state.translateY}px, 0) scale(${state.scale})`;
      modalBody.classList.toggle('is-zoomed', state.scale > MIN_SCALE);
      image.classList.toggle('is-zoomed', state.scale > MIN_SCALE);
    };

    const setScale = (nextScale) => {
      state.scale = Math.max(MIN_SCALE, Math.min(MAX_SCALE, nextScale));
      render();
    };

    const resetZoom = () => {
      state.scale = MIN_SCALE;
      state.translateX = 0;
      state.translateY = 0;
      state.isPointerPanning = false;
      state.isTouchPanning = false;
      state.pinchStartDistance = 0;
      state.pinchStartScale = MIN_SCALE;
      render();
    };

    const closeModal = () => {
      modal.classList.remove('is-open');
      modal.hidden = true;
      resetZoom();

      if (openButton) {
        openButton.setAttribute('aria-expanded', 'false');
      }
    };

    const openModal = () => {
      modal.hidden = false;
      modal.classList.add('is-open');

      if (openButton) {
        openButton.setAttribute('aria-expanded', 'true');
      }
    };

    if (openButton) {
      openButton.addEventListener('click', openModal);
    }

    closeButtons.forEach((button) => {
      button.addEventListener('click', closeModal);
    });

    image.addEventListener('dblclick', (event) => {
      event.preventDefault();
      setScale(state.scale > MIN_SCALE ? MIN_SCALE : 2);
      if (state.scale === MIN_SCALE) {
        state.translateX = 0;
        state.translateY = 0;
      }
      render();
    });

    modalBody.addEventListener(
      'wheel',
      (event) => {
        if (!modal.classList.contains('is-open')) return;

        if (!event.ctrlKey && Math.abs(event.deltaY) < Math.abs(event.deltaX)) return;

        event.preventDefault();
        const zoomStep = event.deltaY < 0 ? 0.2 : -0.2;
        setScale(state.scale + zoomStep);
      },
      { passive: false }
    );

    image.addEventListener('pointerdown', (event) => {
      if (event.pointerType === 'mouse' && state.scale <= MIN_SCALE) return;

      if (state.scale > MIN_SCALE) {
        state.isPointerPanning = true;
        state.panStartX = event.clientX;
        state.panStartY = event.clientY;
        state.panOriginX = state.translateX;
        state.panOriginY = state.translateY;
        image.setPointerCapture(event.pointerId);
      }
    });

    image.addEventListener('pointermove', (event) => {
      if (!state.isPointerPanning || state.scale <= MIN_SCALE) return;

      event.preventDefault();
      state.translateX = state.panOriginX + (event.clientX - state.panStartX);
      state.translateY = state.panOriginY + (event.clientY - state.panStartY);
      render();
    });

    image.addEventListener('pointerup', () => {
      state.isPointerPanning = false;
    });

    image.addEventListener('pointercancel', () => {
      state.isPointerPanning = false;
    });

    image.addEventListener(
      'touchstart',
      (event) => {
        if (!modal.classList.contains('is-open')) return;

        if (event.touches.length === 2) {
          state.pinchStartDistance = distanceBetweenTouches(event.touches[0], event.touches[1]);
          state.pinchStartScale = state.scale;
          state.isTouchPanning = false;
          return;
        }

        if (event.touches.length === 1 && state.scale > MIN_SCALE) {
          state.isTouchPanning = true;
          state.panStartX = event.touches[0].clientX;
          state.panStartY = event.touches[0].clientY;
          state.panOriginX = state.translateX;
          state.panOriginY = state.translateY;
        }
      },
      { passive: false }
    );

    image.addEventListener(
      'touchmove',
      (event) => {
        if (event.touches.length === 2 && state.pinchStartDistance > 0) {
          event.preventDefault();
          const nextDistance = distanceBetweenTouches(event.touches[0], event.touches[1]);
          const ratio = nextDistance / state.pinchStartDistance;
          setScale(state.pinchStartScale * ratio);
          return;
        }

        if (event.touches.length === 1 && state.isTouchPanning && state.scale > MIN_SCALE) {
          event.preventDefault();
          state.translateX = state.panOriginX + (event.touches[0].clientX - state.panStartX);
          state.translateY = state.panOriginY + (event.touches[0].clientY - state.panStartY);
          render();
        }
      },
      { passive: false }
    );

    image.addEventListener('touchend', (event) => {
      if (event.touches.length < 2) {
        state.pinchStartDistance = 0;
      }

      if (event.touches.length === 0) {
        state.isTouchPanning = false;
      }
    });

    image.addEventListener('click', (event) => {
      if (!modal.classList.contains('is-open') || event.detail !== 1) return;

      const now = Date.now();
      if (now - state.lastTapTime <= DOUBLE_TAP_DELAY) {
        event.preventDefault();
        setScale(state.scale > MIN_SCALE ? MIN_SCALE : 2);
        if (state.scale === MIN_SCALE) {
          state.translateX = 0;
          state.translateY = 0;
        }
        render();
      }
      state.lastTapTime = now;
    });

    window.addEventListener('resize', () => {
      if (!modal.hidden) {
        render();
      }
    });

    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && !modal.hidden) {
        closeModal();
      }
    });

    modal.dataset.sizeGuideInit = 'true';
  };

  document.querySelectorAll(MODAL_SELECTOR).forEach(initModal);
})();
